import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { site as defaultSite, waMessage } from "@/config/site";
import { formule as defaultFormule } from "@/config/formule";
import { reviews as defaultReviews, reviewsMeta } from "@/config/reviews";
import { defaultImages, imageLibrary } from "@/config/defaultImages";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "umagazin-admin-token";
const ContentContext = createContext(null);

export const deepMerge = (base, patch) => {
  if (!patch || typeof patch !== "object" || Array.isArray(patch)) return patch ?? base;
  const out = Array.isArray(base) ? [...base] : { ...base };
  for (const key of Object.keys(patch)) {
    const b = out[key];
    const p = patch[key];
    out[key] = b && typeof b === "object" && p && typeof p === "object" && !Array.isArray(p) && !Array.isArray(b) ? deepMerge(b, p) : p;
  }
  return out;
};

// Le liste salvate dal CMS possono contenere foto rimosse dal progetto:
// le scartiamo, mantenendo gli upload caricati dal pannello.
const library = new Set(imageLibrary);
const pruneList = (list) =>
  Array.isArray(list) ? list.filter((src) => !src.startsWith("/images/") || library.has(src)) : list;

const pruneImages = (images) => ({
  ...images,
  hero: pruneList(images.hero),
  sapori: pruneList(images.sapori),
  gallery: pruneList(images.gallery),
});

// Le formule definite nel codice restano sempre presenti: gli override salvati
// dal CMS vengono uniti per id, così una nuova formula appare anche in produzione.
const mergeFormule = (base, patch) => {
  if (!Array.isArray(patch)) return base;
  const byId = new Map(patch.map((f) => [f.id, f]));
  const merged = base.map((f) => (byId.has(f.id) ? { ...f, ...byId.get(f.id) } : f));
  const ids = new Set(base.map((f) => f.id));
  return [...merged, ...patch.filter((f) => !ids.has(f.id))];
};

export const ContentProvider = ({ children }) => {
  const [remote, setRemote] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [admin, setAdmin] = useState(null);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/api/content`)
      .then((r) => setRemote(r.data || {}))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    if (!token) {
      setAdmin(null);
      return;
    }
    axios
      .get(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setAdmin(r.data))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      });
  }, [token]);

  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setEditMode(false);
  }, []);

  const saveText = useCallback(
    async (lang, path, value) => {
      await axios.put(`${API}/api/content/text/field`, { lang, path, value }, auth);
      setRemote((r) => {
        const patch = path.split(".").reverse().reduce((acc, k) => ({ [k]: acc }), value);
        return deepMerge(r, { text: { [lang]: patch } });
      });
    },
    [auth]
  );

  const saveSection = useCallback(
    async (section, value) => {
      await axios.put(`${API}/api/content/${section}`, { value }, auth);
      setRemote((r) => ({ ...r, [section]: value }));
    },
    [auth]
  );

  const changePassword = useCallback(
    (current_password, new_password) =>
      axios.post(`${API}/api/auth/change-password`, { current_password, new_password }, auth),
    [auth]
  );

  const upload = useCallback(
    async (file, onProgress) => {
      const CHUNK = 512 * 1024;
      const total = Math.ceil(file.size / CHUNK);
      const upload_id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      let url = "";
      for (let index = 0; index < total; index++) {
        const blob = file.slice(index * CHUNK, (index + 1) * CHUNK);
        const data = await new Promise((res, rej) => {
          const fr = new FileReader();
          fr.onload = () => res(fr.result.split(",")[1]);
          fr.onerror = rej;
          fr.readAsDataURL(blob);
        });
        const { data: resp } = await axios.post(
          `${API}/api/uploads/chunk`,
          { upload_id, index, total, filename: file.name, data },
          auth
        );
        onProgress?.(Math.round(((index + 1) / total) * 100));
        if (resp.url) url = `${API}${resp.url}`;
      }
      return url;
    },
    [auth]
  );

  const listUploads = useCallback(async () => {
    const { data } = await axios.get(`${API}/api/uploads`);
    return data.map((u) => `${API}${u.url}`);
  }, []);

  const value = useMemo(() => {
    const site = { ...defaultSite, ...(remote.site || {}) };
    const waLink = `https://wa.me/${site.whatsapp}?text=${encodeURIComponent(waMessage)}`;
    return {
      loaded,
      textOverrides: remote.text || {},
      images: pruneImages(deepMerge(defaultImages, remote.images || {})),
      site,
      waLink,
      formule: mergeFormule(defaultFormule, remote.formule),
      reviews: remote.reviews || defaultReviews,
      reviewsMeta: { ...reviewsMeta, ...(remote.reviewsMeta || {}) },
      admin,
      editMode: Boolean(admin) && editMode,
      setEditMode,
      login,
      logout,
      saveText,
      saveSection,
      changePassword,
      upload,
      listUploads,
    };
  }, [remote, loaded, admin, editMode, login, logout, saveText, saveSection, changePassword, upload, listUploads]);

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
};

export const useContent = () => useContext(ContentContext);
